import type { CartLineItem, Product, ProductImage, ProductVariant } from '~/types'
import { buildCustomFieldsKey } from '~/utils/complexProductFields'

/** Flat Redis/API cart line before product hydration */
type ServerCartLine = CartLineItem & {
  productName?: string
  image?: string
}

function minimalProductFromFlat(raw: ServerCartLine): Product {
  const images: ProductImage[] = raw.image
    ? [
        {
          id: 0,
          productId: raw.productId,
          url: raw.image,
          sortOrder: 0,
          isPrimary: true,
        },
      ]
    : []

  return {
    id: raw.productId,
    name: raw.productName || 'Produs',
    slug: '',
    sku: '',
    price: Number(raw.unitPrice) || 0,
    vatRate: 19,
    stock: 0,
    isActive: true,
    isFeatured: false,
    ratingAvg: 0,
    ratingCount: 0,
    images,
    createdAt: '',
    updatedAt: '',
  }
}

/**
 * Server Redis cart stores productName/image; UI expects nested product.
 * Merge server qty/prices with local product when present, else build a minimal product.
 */
export function normalizeCartItem(
  raw: ServerCartLine,
  local?: CartLineItem,
): CartLineItem {
  const product =
    raw.product ??
    local?.product ??
    (raw.productName || raw.image ? minimalProductFromFlat(raw) : undefined)

  const variant =
    (raw.variant as ProductVariant | undefined) ??
    local?.variant

  const customFields = raw.customFields ?? local?.customFields
  const customFieldsKey = raw.customFieldsKey
    ?? local?.customFieldsKey
    ?? buildCustomFieldsKey(customFields)
  const packageItemProductIds = raw.packageItemProductIds ?? local?.packageItemProductIds
  const variationSelections = raw.variationSelections ?? local?.variationSelections

  return {
    productId: raw.productId,
    variantId: raw.variantId,
    quantity: raw.quantity,
    unitPrice: Number(raw.unitPrice),
    totalPrice: Number(raw.totalPrice),
    product,
    variant,
    customFields,
    customFieldsKey,
    customFieldsSnapshot: raw.customFieldsSnapshot ?? local?.customFieldsSnapshot,
    packageItemProductIds,
    variationSelections,
    priceUnit: raw.priceUnit ?? local?.priceUnit ?? product?.priceUnit ?? null,
  }
}

export function normalizeCartItems(
  serverItems: ServerCartLine[],
  localItems: CartLineItem[] = [],
): CartLineItem[] {
  const localByKey = new Map(
    localItems.map((item) => [
      `${item.productId}-${item.variantId ?? 0}-${item.customFieldsKey || ''}`,
      item,
    ]),
  )

  return serverItems.map((raw) => {
    const key = `${raw.productId}-${raw.variantId ?? 0}-${raw.customFieldsKey || buildCustomFieldsKey(raw.customFields) || ''}`
    return normalizeCartItem(raw, localByKey.get(key))
  })
}
